import { Box, Chip } from "@mui/material";
import { styled } from "@mui/material/styles";

interface ChipContainerProps {
  hasDate?: boolean;
  isOpen?: boolean;
}

export const ChipContainer = styled(Chip, {
  shouldForwardProp: (prop) => prop !== "hasDate" && prop !== "isOpen",
})<ChipContainerProps>(({ theme, hasDate, isOpen }) => ({
  borderRadius: "100px",
  paddingTop: "3px",
  paddingBottom: "3px",
  paddingLeft: "4px",
  paddingRight: "4px",
  height: "24px",
  minHeight: "24px",
  maxHeight: "24px",
  fontSize: theme.typography.body2.fontSize,
  fontWeight: hasDate ? theme.typography.fontWeightMedium : theme.typography.fontWeightRegular,
  cursor: "pointer",
  transition: theme.transitions.create(["background-color", "border-color", "box-shadow"], {
    duration: theme.transitions.duration.short,
  }),

  // Initial state (no date): dashed border, white background
  ...(!hasDate && {
    borderStyle: "dashed",
    backgroundColor: theme.palette.background.paper,
  }),

  // Date selected state: solid border, no background
  ...(hasDate && {
    borderStyle: "solid",
    backgroundColor: "transparent",
  }),

  // Hover state or picker open (no date): grey background
  ...(!hasDate && {
    "&:hover": {
      backgroundColor: theme.palette.action.hover,
    },
  }),

  // Hover state (with date): keep transparent
  ...(hasDate && {
    "&:hover": {
      backgroundColor: "transparent",
      borderColor: theme.palette.primary.main,
    },
  }),

  // When picker is open (no date): grey background
  ...(isOpen &&
    !hasDate && {
      backgroundColor: theme.palette.action.hover,
    }),

  "&:focus-visible": {
    outline: `2px solid ${theme.palette.primary.main}`,
    outlineOffset: 2,
  },

  "&.Mui-disabled": {
    opacity: 0.6,
    cursor: "not-allowed",
    pointerEvents: "auto",
  },

  "& .MuiChip-icon": {
    fontSize: 18,
    marginLeft: theme.spacing(0.5),
  },

  "& .MuiChip-deleteIcon": {
    fontSize: 18,
    marginRight: theme.spacing(0.5),
    "&:hover": {
      color: theme.palette.error.main,
    },
  },
}));

export const DateLabel = styled(Box)(({ theme }) => ({
  display: "inline",
  "& .label-text": {
    color: theme.palette.text.secondary,
    fontWeight: theme.typography.fontWeightRegular,
  },
  "& .date-value": {
    color: theme.palette.text.primary,
    fontWeight: theme.typography.fontWeightMedium,
    marginLeft: theme.spacing(0.5),
  },
}));
